import { z } from 'zod';

export const PAYOUT_TYPE_OPTIONS = [
  { value: 'interest', label: 'Interest' },
  { value: 'principal', label: 'Principal' },
];

// v1.16.0 — breaking change. A payout is now either principal OR interest,
// stated explicitly by the admin via `type`, never both in one call and
// never inferred. `amount` is a single positive number whose meaning
// (principal vs interest) is entirely determined by `type`.
//
// Only principal is bounded client-side — against the investment's
// outstanding payout balance (principal minus prior principal payouts,
// passed in as `remainingPrincipal`, distinct from the investment's own
// `remaining_amount`, which tracks funding). Interest is deliberately
// uncapped: the backend allows flexible amounts/timing at admin discretion
// (e.g. one lump sum covering several missed months), so there is no
// client-side max for it. The server re-validates on submit either way;
// this only saves an obviously-doomed round trip for the principal case.
//
// `type` is never derived from the entered amount here or anywhere else —
// the backend genuinely cannot categorize an amount without being told,
// which is why this field is required rather than optional/inferred.
export function buildRecordPayoutSchema(remainingPrincipal) {
  const maxPrincipal = remainingPrincipal != null ? Number(remainingPrincipal) : null;
  const todayIso = new Date().toISOString().slice(0, 10);

  return z
    .object({
      type: z.enum(['principal', 'interest'], { errorMap: () => ({ message: 'Select a payout type' }) }),
      amount: z.coerce.number().positive('Enter an amount greater than zero.'),
      paid_at: z
        .string()
        .min(1, 'Select a date')
        .refine((v) => v <= todayIso, { message: 'Date paid cannot be in the future.' }),
      note: z.string().max(2000, 'Note is too long.').optional(),
    })
    .refine((data) => data.type !== 'principal' || maxPrincipal == null || data.amount <= maxPrincipal, {
      message: `Principal cannot exceed the outstanding balance${maxPrincipal != null ? ` (${maxPrincipal.toLocaleString()})` : ''}`,
      path: ['amount'],
    });
}

export default buildRecordPayoutSchema;
